import { Tag, Divider, Table, Col, Row, Typography, Space } from 'antd'
import dayjs from 'dayjs'
import { DATEFORMAT } from '@constants/dateformat'
import Descriptions from '@components/common/Descriptions'
import Title from '@components/common/Title'
import { CustomDescriptionType } from '@/types/IDescriptions'
import { useCompany } from '@pages/Company'
import { columns } from '@components/Company/Descriptions'
import RenderPage from '@components/common/RenderPage'
import { useTranslation } from 'react-i18next'
import { ORDER_STATUS, ORDER_TYPES } from '@constants/orderstatus'
import type {
  IOrder,
  IItems,
  IProduct,
  IItemOrderRequest,
  IOrderRequest
} from '@/types/IOrder'
import type { ColumnsType } from 'antd/es/table'
import { translateColumns } from '@helpers/translateColumns'
import { useParams } from 'react-router-dom'
import useFetch from '@hooks/useFetch'
import { api } from '@helpers/api'
import OrderDrawer from '@components/Order/OrderDrawer'
import { useGenericContext } from '@hooks/useGenericContext'
import { OrderContext } from '@contexts/OrderContext'
import Icon from '@components/common/Icon'
import Button from '@components/common/Button'

const fetchOrder = (uuid: string) => {
  const { data, loading, error } = useFetch<IOrder, undefined>({
    func: () => api.get(`/order/${uuid}`),
  })
  return { data, loading, error }
}

const generateRandomFormNumber = () => {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return (array[0] % 10000).toString();
}

export const transformOrderRequest = (orderRequest: IOrderRequest) => {
  const transformItem = item => {
    if (item.children?.length === 0) {
      delete item.children
    } else if (item.children?.items) {
      item.children = item.children.items
    }

    if (Array.isArray(item.children)) {
      item.children = item.children.map(transformItem)
    }

    return item
  }

  orderRequest.items = orderRequest.items.map(transformItem)
  return orderRequest
}

const ColumnTranslation = ({
  text,
  constant
}: {
  text: string
  constant: any
}) => {
  const { t } = useTranslation()
  return <Tag color={constant[text]}>{t(text)}</Tag>
}

const priceColumns: CustomDescriptionType<IOrder>[] = [
  {
    span: 1,
    label: 'TOTAL_PRICE',
    key: 'price_total',
    render: data => (
      <Typography.Text strong>{`R$ ${data.data}`}</Typography.Text>
    )
  },
  {
    span: 1,
    label: 'STATUS',
    key: 'status',
    render: status => (
      <ColumnTranslation text={status.data} constant={ORDER_STATUS} />
    )
  },
  {
    label: 'TYPE',
    key: 'type',
    render: type => (
      <ColumnTranslation text={type.data} constant={ORDER_TYPES} />
    )
  },
  {
    span: 1,
    label: 'UPDATEAT',
    key: 'updated_at',
    render: updated_at =>
      updated_at.data ? dayjs(updated_at.data).format(DATEFORMAT) : ''
  },
  {
    span: 1,
    label: 'OBS',
    key: 'obs'
  },
  {
    span: 1,
    label: 'DISCOUNT',
    key: 'discount_percents',
    render: discount_percents => (
      <>
        {discount_percents && (
          <Typography.Text>{`${Math.floor(discount_percents.data)}%`}</Typography.Text>
        )}
      </>
    )
  },
  {
    span: 1,
    label: 'DISCOUNT_MOTIVE',
    key: 'discount_motive'
  }
]

export const productColumns: CustomDescriptionType<IProduct>[] = [
  {
    span: 1,
    label: 'NAME',
    key: 'name'
  },
  {
    span: 1,
    label: 'DC',
    key: 'dc',
    render: ({ data }) => data?.shortname
  }
]

const commonItemColumns: ColumnsType<IItemOrderRequest> = [
  {
    title: 'NAME',
    dataIndex: 'item',
    key: 'item',
    render: item => <span>{item.override_name ?? item.item}</span>
  },
  {
    title: 'CURRENT',
    dataIndex: 'current',
    key: 'current',
    render: (_, data) => (
      <>
        <span>{`${data.current}`}</span>
        {data.item?.postfix && <span>{` ${data.item.postfix}`}</span>}
      </>
    )
  }
];

export const itemsColumns: ColumnsType<IItemOrderRequest> = [
  ...commonItemColumns,
  {
    title: 'PRICE',
    dataIndex: 'value',
    key: 'value',
    render: value => <Typography.Text>{`R$ ${value}`}</Typography.Text>
  }
]

export const relatedProductsColumns: ColumnsType<IItemOrderRequest> = [
  ...commonItemColumns
]

export const relatedProductColumns: ColumnsType<any> = [
  {
    title: 'INDEX',
    dataIndex: 'index',
    key: 'index',
    render: (_, record) => <span>{record.index}</span>
  },
  {
    title: 'CURRENT',
    dataIndex: 'current',
    key: 'current',
    render: (_, record) => <span>{record.current}</span>
  }
]

interface OrderHeadProps {
  data: IOrder;
  page?: boolean;
}

export const OrderHead = ({ data, page }: OrderHeadProps) => {
  const { openDrawer } = useGenericContext(OrderContext)
  const { t } = useTranslation()

  const onClick = () => {
    openDrawer(data)
  }

  const title = (
    <Title
      name={data.company?.name ? data.company.name : data.customer.name}
      style={{ margin: 0 }}
    />
  )

  if (!page) {
    return title
  }

  return (
    <Row align="middle" justify={'space-between'} gutter={[8, 8]} style={{
      marginBottom: 10,
    }}>
      {title}
      <Col xs={24} md={6} xl={6}>
        <Button
          style={{ paddingInline: 47 }}
          color='primary'
          icon={<Icon name="fa-light fa-edit" color="white" />}
          onClick={onClick}
          data-cy="edit-order"
          block
        >
          {t('EDIT_ORDER')}
        </Button>
      </Col>
    </Row>
  )
}

interface RelatedProductTagsProps {
  items: any[];
  relatedProducts?: any[];
  t: (key: string) => string;
}

const RelatedProductTags = ({ items, relatedProducts, t }: RelatedProductTagsProps) => {
  return (
    <Divider>
      <Space style={{ marginBottom: '10px' }}>
        <Title>{t('RELATED_PRODUCT')}</Title>
        {items
          .filter((item) => item.related_product)
          .map((item) => {
            const relatedProductData = relatedProducts?.find(
              (rp) => rp.product.uuid === item.related_product.uuid,
            )
            return (
              <Tag key={item.uuid} color="green">
                {relatedProductData?.product?.name ?? 'Unknown Product'}
              </Tag>
            )
          })}
      </Space>
    </Divider>
  )
}

interface RelatedProductTablesProps {
  items: any[];
  columns: ColumnsType<any>;
}

const RelatedProductTables = ({ items, columns }: RelatedProductTablesProps) => {
  const filteredItems = items.filter((item) => item.related_product)

  return (
    <>
      {filteredItems.map((item, index) => (
        <div key={item.uuid}>
          <Table
            dataSource={item.related_product.items}
            columns={columns}
            rowKey={(record) => record.uuid}
            pagination={false}
          />
          {index < filteredItems.length - 1 && <Divider />}
        </div>
      ))}
    </>
  )
}

interface RelatedProductsSectionProps {
  relatedProducts: any[];
  columns: ColumnsType<any>;
  t: (key: string) => string;
}

const RelatedProductsSection = ({ relatedProducts, columns, t }: RelatedProductsSectionProps) => {
  if (!relatedProducts || relatedProducts.length === 0) return null

  return (
    <>
      <Divider />
      <Divider>
        <Title
          style={{
            marginTop: '20px',
            marginBottom: '10px',
          }}
        >
          {t('RelatedProducts')}
        </Title>
      </Divider>
      {relatedProducts.map((relatedProduct) => (
        <div key={relatedProduct.uuid}>
          <Table
            dataSource={transformOrderRequest(relatedProduct).items}
            columns={columns}
            rowKey="uuid"
            pagination={false}
          />
        </div>
      ))}
    </>
  )
}

interface OrderItemProps {
  item: IItems;
  translatedColumns: {
    items: ColumnsType<any>;
    relatedProducts: ColumnsType<any>;
    relatedProduct: ColumnsType<any>;
  };
  t: (key: string) => string;
}

const OrderItem = ({ item, translatedColumns, t }: OrderItemProps) => {
  const hasRelatedProductItems = item?.order_request?.items?.some(
    (orderItem) => orderItem.item?.related_product
  )

  return (
    <Col key={item.uuid} span={24}>
      <Descriptions
        columns={productColumns}
        bordered
        dataSource={item.product}
        size="small"
        column={{ xs: 1, xl: 4 }}
      />

      <Table
        dataSource={transformOrderRequest(item.order_request).items}
        columns={translatedColumns.items}
        rowKey="uuid"
        pagination={false}
      />

      <Divider />
      <Divider>
        <Space>
          <Title>{t('SUPPORT_LEVEL')}</Title>
          <Tag color="blue">{item.order_request.support_level}</Tag>
        </Space>
      </Divider>

      {hasRelatedProductItems && (
        <>
          <Divider />
          <RelatedProductTags
            items={item.order_request.items}
            relatedProducts={item.order_request.related_products}
            t={t}
          />
          <RelatedProductTables
            items={item.order_request.items}
            columns={translatedColumns.relatedProduct}
          />
        </>
      )}

      <RelatedProductsSection
        relatedProducts={item.order_request.related_products}
        columns={translatedColumns.relatedProducts}
        t={t}
      />
    </Col>
  )
}

interface OrderContentProps {
  data: IOrder;
  dataSource: any;
  loading?: boolean;
}

const OrderContent = ({ data, dataSource, loading }: OrderContentProps) => {
  const { t } = useTranslation()

  const translatedColumns = {
    items: translateColumns(itemsColumns, t),
    relatedProducts: translateColumns(relatedProductsColumns, t),
    relatedProduct: translateColumns(relatedProductColumns, t)
  }

  return (
    <RenderPage data={dataSource} loading={loading}>
      <Descriptions
        columns={columns}
        bordered
        dataSource={dataSource}
        size="small"
      />

      <Divider>{t('PRICE')}</Divider>
      <Descriptions
        columns={priceColumns}
        bordered
        dataSource={data}
        size="small"
        column={{ xs: 1, xl: 4 }}
      />

      <Divider>{t('PRODUCTS')}</Divider>
      <Row gutter={[16, 16]}>
        {data.items.map((item: IItems) => (
          <OrderItem
            key={item.uuid}
            item={item}
            translatedColumns={translatedColumns}
            t={t}
          />
        ))}
      </Row>
    </RenderPage>
  )
}

export const OrderByFetch = () => {
  const { uuid } = useParams()
  const { data, loading, error } = uuid ? fetchOrder(uuid) : { data: null, loading: false, error: 'UUID is undefined' }
  const numForm = generateRandomFormNumber()

  return (
    <RenderPage data={data} loading={loading} error={error}>
      <OrderDrawer order={data} action={'edit'} numForm={numForm}>
        <OrderHead data={data} page />
        <OrderContent
          data={data}
          dataSource={data?.company || data?.customer}
        />
      </OrderDrawer>
    </RenderPage>
  )
}

export const Order = ({ data }: { data: IOrder }) => {
  const { company, loading } = useCompany(data?.company?.uuid)
  const dataSource = company?.uuid ? company : data?.customer

  return (
    <OrderContent
      data={data}
      dataSource={dataSource}
      loading={loading}
    />
  )
}

export default Order
